// src/pages/Login.tsx  (logic unchanged, only the layout and sizes)
import { Mail } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardEyebrow, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Field, InputWithIcon } from '../components/ui/input'
import { ApiError, post, rateLimitMessage } from '../lib/api'
import {
  GateClosed,
  GateLoading,
  GateOpensSoon,
  isWindowRefusal,
  useRegistrationGate,
} from '../components/RegistrationGate'
import { savePending } from '../lib/session'
import AuthLayout from '../components/AuthLayout'

interface LoginResponse {
  status: 'otp_sent'
  maskedEmail: string
  resendInSeconds: number
  verified: boolean
}

export default function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  /**
   * The same registration window that governs the sign-up page — but only its
   * *opening* time applies here.
   *
   * Before the window opens there is nothing to sign in to, so this page shows
   * the countdown. Once it has closed, signing in keeps working: by then the
   * students who registered hold a QR pass that is scanned at every booth and
   * carries their certificate, and closing sign-in would lock out anyone who
   * cleared their browser or swapped phone mid-event. Only *new sign-ups*
   * stop at the closing time.
   */
  const gate = useRegistrationGate()
  const beforeOpening = gate.status === 'before'
  /** Sign-in still works; only the invitation to register has to go. */
  const registrationClosed = gate.status === 'closed'

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const address = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setError('Enter a valid e-mail address.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await post<LoginResponse>('/api/student/login', { email: address })
      savePending({
        email: address,
        maskedEmail: res.maskedEmail,
        resendInSeconds: res.resendInSeconds,
        accountExists: res.verified,
      })
      navigate('/verify')
    } catch (err) {
      const message = (err as ApiError).message

      setError(
        // A rate-limit refusal is not a failure, so it gets its own wording.
        rateLimitMessage(err) ??
        (message === 'not_registered'
          ? // Telling them to register is only useful if they still can.
            registrationClosed
            ? 'No account uses that address. Registration has closed, so a new one cannot be created — please see the help desk at the venue.'
            : 'No account uses that address yet. Register first — it takes about a minute.'
          : isWindowRefusal(message)
            ? 'Sign-in is not open yet.'
            : 'Something went wrong. Please try again, or visit the help desk.'),
      )

      /*
       * The window shut while this page was open — re-read it so the card
       * turns over into the countdown or the closed notice, rather than
       * leaving a form that will be refused however many times it is sent.
       */
      if (isWindowRefusal(message)) gate.refetch()
    } finally {
      setSubmitting(false)
    }
  }

  /*
   * Held back until the window is known: showing the form and swapping it a
   * moment later for a countdown reads as a broken page.
   */
  if (gate.status === 'loading') {
    return (
      <AuthLayout>
        <GateLoading />
      </AuthLayout>
    )
  }

  /*
   * Only the countdown. There is no closed state on this page — see above:
   * after the window closes this is the one door that stays open.
   */
  if (beforeOpening) {
    return (
      <AuthLayout>
        <GateOpensSoon
          audience="login"
          opensAt={gate.window?.opensAt ?? null}
          seconds={gate.untilOpen ?? 0}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-sm rounded-2xl border border-white bg-white/90 text-slate-900 shadow-xl shadow-blue-900/10">
        <CardHeader className="space-y-1 px-6 pb-2 pt-6">
          <CardEyebrow className="text-[11px] font-bold uppercase tracking-widest text-orange-600">
            Login to your pass
          </CardEyebrow>
          <CardTitle className="text-2xl font-bold text-[#0B1E3F]">Open your pass</CardTitle>
          <CardDescription className="text-sm text-slate-500">
            Enter the e-mail you registered with and we will send a secure code.
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6 pt-3">
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <Field label="Email Address" htmlFor="l-email" error={error ?? undefined}>
              <InputWithIcon
                icon={Mail}
                id="l-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null) }}
                autoComplete="email"
                aria-invalid={!!error}
                placeholder="you@example.com"
                className="h-11 rounded-lg border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-blue-500/20"
              />
            </Field>

            <Button
              type="submit"
              block
              disabled={submitting}
              className="h-11 rounded-lg bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:opacity-70"
            >
              {submitting ? 'Sending secure code…' : 'Send me a code'}
            </Button>
          </form>

          {/*
            Once registration has closed, "Register here" leads to a closed
            door. Say so instead of sending them there to find out.
          */}
          {registrationClosed ? (
            <p className="mt-5 text-center text-sm text-slate-500">
              Registration has closed — this page is for students who signed up before then. If
              you did not, the help desk at the venue can assist you.
            </p>
          ) : (
            <p className="mt-5 text-center text-sm text-slate-600">
              Not registered yet?{' '}
              <Link to="/" className="font-semibold text-orange-600 hover:underline">
                Register here
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
